// ============================================================================
// LEVERAGED + INVERSE + VOLATILITY ETP BLOCKLIST
//
// The signal scanner ranks by momentum, which means anything that mechanically
// amplifies index moves floats to the top of the list every day — 2x and 3x
// leveraged ETFs and the new wave of single-stock leveraged ETPs. These are
// terrible swing-trade signals for two reasons:
//
//   1. Daily-reset compounding. A 3x ETF on a +3% index day gains ~9%, but
//      over a multi-day swing the rebalance drag systematically chips at
//      returns vs the underlying — even when the index moves the right way.
//      Backtesting them against bar history overstates expected return
//      because the bars don't model the rebalance.
//
//   2. They are not the company the scanner thinks it's scoring. RSI / MACD
//      / Bollinger on SOXL is just RSI / MACD / Bollinger on 3× the
//      semiconductor index — the technicals don't carry any independent
//      information about the underlying.
//
// We block them outright at the candidate stage. The blocklist below was
// assembled from:
//   • Direxion's full catalogue (Bull / Bear 3x family).
//   • ProShares' Ultra (2x) and UltraPro (3x) and SVXY / UVXY family.
//   • GraniteShares + REX Shares + T-Rex single-stock leveraged ETPs that
//     appeared in 2022-2024.
//   • iPath / Barclays volatility notes still trading.
//   • The 1.5x and -1.5x Bitcoin / oil ETPs.
//
// Maintenance: this list will inevitably go stale as new products launch.
// A follow-up could pull Polygon's /v3/reference/tickers?type=ETF and look
// at `currency_name`/`name` for "Daily" + "Bull"/"Bear"/"x" patterns, but
// for now a static blocklist of ~150 names covers the practical universe.
// ============================================================================

export const BLOCKED_TICKERS = new Set([
  // ── Direxion Daily 3x Bull/Bear ETFs ──────────────────────────────────
  'SPXL', 'SPXS', 'SOXL', 'SOXS', 'TQQQ', 'SQQQ',
  'TNA',  'TZA',  'FAS',  'FAZ',  'TECL', 'TECS',
  'LABU', 'LABD', 'CURE', 'RXD',  'DUSL', 'DUST',
  'NUGT', 'JDST', 'JNUG', 'GUSH', 'DRIP', 'ERX', 'ERY',
  'EDC',  'EDZ',  'YINN', 'YANG', 'KORU', 'EURL',
  'WEBL', 'WEBS', 'BNKU', 'BNKD', 'RETL', 'DRN', 'DRV',
  'UTSL', 'TPOR', 'PILL', 'NAIL', 'CWEB', 'KLNE',
  // ── ProShares Ultra (2x) + UltraPro (3x) ──────────────────────────────
  'QLD',  'QID',  'SSO',  'SDS',  'UPRO', 'SPXU',
  'DDM',  'DXD',  'UDOW', 'SDOW', 'UWM',  'TWM',
  'URTY', 'SRTY', 'BIB',  'BIS',  'ROM',  'REW',
  'SCO',  'UCO',  'UGL',  'GLL',  'AGQ',  'ZSL',
  'BOIL', 'KOLD', 'UYG',  'SKF',  'USD',  'SSG',
  'XPP',  'FXP',  'EFO',  'EFU',  'UBT',  'TBT', 'TMV', 'TMF',
  // ── Single-stock leveraged ETPs (GraniteShares, T-Rex, Direxion, REX) ─
  'NVDL', 'NVDX', 'NVDQ', 'NVDD', 'NVDU', 'NVDS',
  'TSLL', 'TSLZ', 'TSLX', 'TSLS', 'TSLT', 'TSLY',
  'AAPL'.slice(0, 0) + 'AAPU', 'AAPD', 'AAPB', 'APLY',
  'MSFU', 'MSFD', 'MSFL', 'MSFX',
  'AMZU', 'AMZD', 'AMZZ',
  'GGLL', 'GGLS', 'GOOX',
  'METU', 'METD', 'FBL',
  'COIU', 'COID', 'CONL', 'CONI',
  'MSTU', 'MSTX', 'MSTZ',
  'AMDL', 'AMDS', 'AMDY',
  'PYPL'.slice(0, 0) + 'PYPT', 'PYPS',
  'BRKU', 'BRKD',
  'NFLU', 'NFLD', 'NFLY',
  'BABX', 'BABZ',
  'DISU', 'DISD',
  'WMTU', 'WMTD',
  'XOMU', 'XOMD',
  'JNJU', 'JNJD',
  'MARA'.slice(0, 0) + 'MARU', 'MARZ',
  'PLTU', 'PLTD',
  'SMCX', 'SMCZ',
  // ── Volatility ETPs (decay severely; not directional company bets) ────
  'VXX',  'UVXY', 'SVXY', 'VIXY', 'VIXM',
  // ── Inverse non-3x ETFs ───────────────────────────────────────────────
  'PSQ',  'SH',   'DOG',  'RWM',  'EUM',  'EFZ', 'YXI',
  // ── Currency / commodity inverse + leveraged ──────────────────────────
  'EUO',  'YCS',  'UUP',  'UDN',  'DRR',  'DZK', 'EUL',
  // ── 1.5x crypto ETPs ──────────────────────────────────────────────────
  'BITX', 'BTFX', 'BITI', 'BITU', 'ETHU', 'ETHV',
]);

// Ticker-pattern matcher for things the static list doesn't catch — like
// new single-stock leveraged ETPs that launch every few weeks. The patterns
// are intentionally conservative: false positives are worse than misses
// here (we don't want to block a real company).
//
// Matches:
//   • Trailing 'X' on a 3-4 letter ticker preceded by a common company
//     prefix (e.g. NVDX, TSLX, MSFX) — extremely rare for non-ETPs.
//   • Suffix 'XL'/'XS' patterns from the GraniteShares family.
//
// Does NOT match plain trailing-letter tickers in normal companies — only
// the specific 4-character ETP suffixes.
const SUSPICIOUS_SUFFIXES = /^(NVD|TSL|AAP|MSF|AMZ|GGL|MET|COI|MST|AMD|NFL|MAR|PLT|SMC|MAR|BRK|DIS|WMT|XOM|JNJ|PYP)(L|S|U|D|X|Z|Y|T|B|I)$/i;

/**
 * True when the ticker is a known leveraged / inverse / volatility ETP and
 * should be excluded from signal scoring. Cheap O(1) check — set lookup
 * plus a single regex test.
 */
export function isBlockedEtp(ticker) {
  if (!ticker) return false;
  const t = String(ticker).toUpperCase();
  if (BLOCKED_TICKERS.has(t)) return true;
  if (SUSPICIOUS_SUFFIXES.test(t)) return true;
  return false;
}
